import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Bookmark } from 'src/app/shared/data/data.generated';
import { ApiService } from 'src/app/shared/services/api.service';
import {
  Filter,
  Table,
  TableResult,
  TableView,
} from 'src/app/shared/services/api/table';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { FormBuilder } from '@angular/forms';
import { ActiveChipFilters, Chip } from 'src/app/shared/components/hrt-filter-chips/hrt-filter-chips.component';
import {
  ColumnDefinitionV3,
  ColumnRendererComponentV3,
  TableServiceV3,
} from '@gea/digital-ui-lib';
import { BookmarkActionsRenderer } from './bookmark-renderers/bookmark-actions-renderer.component';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
  selector: 'app-bookmarks',
  templateUrl: './bookmarks.component.html',
  styleUrls: ['./bookmarks.component.scss'],
  standalone: false,
})
export class BookmarkComponent implements OnInit {
  constructor(
    private api: ApiService,
    public dialog: MatDialog,
    private loader: LoaderService,
    private fb: FormBuilder,
    private utilityService: UtilityService,
    private tableService: TableServiceV3,
  ) {}

  public listOptions: TableView = new TableView();
  public bookmarkItems: Bookmark[] = [];
  bookmarkTable: Table<Bookmark> = new Table<Bookmark>(this.loader);
  searchForm = this.fb.group({
    search: [''],
  });
  searchInput = new Subject<string>();
  destroy$ = new Subject<void>();
  tableId: string = 'hrtc-booksmarks-table';
  isTableLoading: boolean = true;
  bookmarkSearch?: string;
  sortKey?: string;
  sortOrder?: string;
  page: number = 0;
  pageSize: number = 10;
  filters: Filter = {};
  count = 0;
  activeChipFilters: ActiveChipFilters = { searchText: '', filters: {} };
  public columnDefinitions: ColumnDefinitionV3[] = [
    {
      displayName: 'Location',
      key: 'BookmarkType',
      sortable: false,
      minWidth: 200,
    },
    {
      displayName: 'Name',
      key: 'BookmarkName',
      sortable: false,
      minWidth: 200,
    },
    {
      displayName: 'URL',
      key: 'URL',
      sortable: false,
      minWidth: 200,
    },
    {
      displayName: 'Actions',
      key: 'Actions',
      sortable: false,
      minWidth: 200,
      renderer: {
        component: BookmarkActionsRenderer as ColumnRendererComponentV3<any>,
        config: {
          refreshBookmarksTable: (data: TableResult<Bookmark>) => {
            (this.bookmarkItems = data.Data ?? []),
              (this.count = data.Count ?? 0);
          },
        },
      },
    },
  ];
  public nonClickableColumnKeys = ['Actions'];
  searchPlaceholder = 'Search Bookmarks';

  ngOnInit(): void {
    this.handleHeaderClick();
    this.handleSearchChanged();
  }

  private handleHeaderClick() {
    this.tableService
      .getFilterTableSettings(this.tableId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        if (!settings?.columns) return;
        if (settings?.columns) {
          this.filters = this.buildBackendFiltersFromColumns(
            settings.columns,
            this.columnDefinitions,
          );
        }
        if (settings?.page !== undefined) {
          this.page = settings.page;
        }
        this.pageSize = settings?.pageSize ?? this.pageSize;
        const sortedColumn = Object.keys(settings.columns).find(
          (key) => settings.columns[key].sort ?? '',
        );

        if (sortedColumn) {
          this.sortKey = sortedColumn;
          if (settings.columns[sortedColumn].sort) {
            this.sortOrder =
              settings.columns[sortedColumn].sort === 'desc' ? 'True' : 'False';
          } else {
            this.sortOrder = '';
          }
        }

        this.activeChipFilters.filters = settings?.columns
          ? settings.columns
          : this.activeChipFilters.filters;

        this.fetchBookmarks();
      });
  }

  private handleSearchChanged(): void {
    this.searchInput.pipe(debounceTime(500)).subscribe((searchTerm: string) => {
      if (searchTerm.length >= 3 || searchTerm.length == 0) {
        this.bookmarkSearch = searchTerm;
        this.utilityService.setTableSearchSetting(
          this.tableId,
          this.bookmarkSearch,
        );
        this.page = 0; // Reset to first page on new table search
        this.fetchBookmarks();
      }
    });
  }

  fetchBookmarks(): void {
    this.isTableLoading = true;
    if (this.utilityService.getTableSearchSetting(this.tableId)) {
      this.bookmarkSearch = this.utilityService.getTableSearchSetting(
        this.tableId,
      );
    }
    const listOptions: TableView = {
      Search: this.bookmarkSearch ?? '',
      SortColumn: this.sortKey ?? '',
      SortDesc: this.sortOrder ?? '',
      PageSize: this.pageSize,
      Page: this.page,
      HasPaging: true,
      Filters: this.filters || {},
      PageSizeOptions: [10, 20, 50],
      ClientFilters: [],
      ServerFilters: [],
    };
    this.api.Bookmark.GetBookmarks(listOptions).subscribe({
      next: (response: TableResult<Bookmark>) => {
        this.bookmarkItems = response.Data ?? [];
        this.activeChipFilters.searchText = this.bookmarkSearch ?? '';
        this.count = response.Count;
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.isTableLoading = false;
      },
    });
  }

  searchValueChanged(newValue: string): void {
    this.searchInput.next(newValue);
  }

  buildBackendFiltersFromColumns(columns: any, columnDefinitions: any[]): any {
    const backendFilters: any = {};

    columnDefinitions.forEach((colDef) => {
      const key = colDef.key;
      const col = columns[key];
      if (
        col &&
        col.filter &&
        Array.isArray(col.filter) &&
        col.filter.length > 0
      ) {
        // Find options for this column if available
        const options = colDef.filter?.config?.options || [];
        const values = col.filter.map((val: any) => {
          // Try to find a matching option for key/label mapping
          const found = options.find(
            (opt: any) =>
              opt.value?.toString() === val?.toString() ||
              opt.key?.toString() === val?.toString() ||
              opt.label === val ||
              opt.name === val,
          );
          return found
            ? {
                key: found.key ?? found.value ?? val,
                label: found.label ?? found.name ?? val,
              }
            : { key: val, label: val };
        });

        backendFilters[key] = {
          isClientSide: false,
          type: 'multiSelect',
          values,
        };
      }
    });

    return backendFilters;
  }

  onChipRemoved(chip: Chip) {
      if (chip && chip.chipKey === 'clearAll') {
        // Clear all filters
        this.activeChipFilters = { searchText: '', filters: {} };
        this.bookmarkSearch = '';
        this.sortKey = '';
        this.sortOrder = '';
        this.filters = {};
        this.utilityService.setTableSearchSetting(this.tableId, '');
        this.searchForm.get('search')?.setValue('');
        this.tableService.setFilterTableSettings(this.tableId, {
          columns: this.activeChipFilters.filters,
          page: this.page,
          pageSize: this.pageSize,
        });
      } else if (
        chip &&
        chip.chipKey === 'searchText' &&
        chip.chipValue !== '' &&
        this.activeChipFilters.searchText === chip.chipValue
      ) {
        // Clear user search
        this.bookmarkSearch = '';
        this.utilityService.setTableSearchSetting(this.tableId, '');
        this.activeChipFilters.searchText = '';
        this.tableService.setFilterTableSettings(this.tableId, {
          columns: this.activeChipFilters.filters,
          page: this.page,
          pageSize: this.pageSize,
        });
        this.searchForm.get('search')?.setValue('');
      } else {
        if (chip && chip.chipKey !== 'searchText' && chip.chipValue !== '') {
          // 1. If chipValue is 'asc' or 'desc', remove "sort chip" and update documents
          if (chip.chipValue === 'asc' || chip.chipValue === 'desc') {
            if (this.activeChipFilters.filters[chip.chipKey]) {
              this.activeChipFilters.filters[chip.chipKey].sort = '';
              this.sortKey = '';
              this.sortOrder = '';
              this.tableService.setFilterTableSettings(this.tableId, {
                columns: this.activeChipFilters.filters,
                page: this.page,
                pageSize: this.pageSize,
              });
              this.fetchBookmarks();
            }
          } else {
            // 2. Remove "filter chip" for chipKey with chipValue
            if (
              this.activeChipFilters.filters[chip.chipKey] &&
              Array.isArray(this.activeChipFilters.filters[chip.chipKey].filter)
            ) {
              this.activeChipFilters.filters[chip.chipKey].filter =
                this.activeChipFilters.filters[chip.chipKey].filter.filter(
                  (val: any) => val !== chip.chipValue,
                );
              // Rebuild backend filters and update documents
              this.filters = this.buildBackendFiltersFromColumns(
                this.activeChipFilters.filters,
                this.columnDefinitions,
              );
              this.tableService.setFilterTableSettings(this.tableId, {
                columns: this.activeChipFilters.filters,
                page: this.page,
                pageSize: this.pageSize,
              });
              this.fetchBookmarks();
            }
          }
        }
      }
    }

  onClick(rowData: any) {
    if (!rowData.URL) return;
    window.open(rowData.URL, '_self');
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.searchInput.complete();
  }
}
